import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Row,
  Table,
} from "reactstrap";
import api from "../../api";
import { useAppDialog } from "./AppDialogProvider";
import { getMafiaEmpId } from "./mafiaIdentity";
import useAuth from "../Personal Attendance/useAuth";
import { MafiaPageHeader } from "./_shared";
import MafiaTheme from "./MafiaTheme";

export default function FrmJob({ onBack }) {
  const dialog = useAppDialog();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const [searchJobNum, setSearchJobNum] = useState("");
  const searchInputRef = useRef(null);
  const rowRefs = useRef({});
  const mountedRef = useRef(true);

  const [currentUsername, setCurrentUsername] = useState("");

  // Legacy frmJob.vb lines 6-10 hardcoded `a.naglapura` / `joseph.regan` as
  // the only users who could see the Reset Job button. The earlier modern
  // port copied this literal check, but `getCurrentUsername` now returns an
  // EmpId rather than a Windows login, so the check matched nobody.
  //
  // Replaced with IT Admin gate. Reset Job destructively wipes 4 tables of
  // recorded inspection data; the backend endpoints (delete-jobheader,
  // delete-opheader, delete-mafiaentries, delete-mafiaentries-comments) are
  // independently gated to [Authorize(Roles = "IT Admin")] in
  // MafiaController.cs — frontend hiding is defense in depth, not the
  // security boundary. See PHASE4_MAFIA_AUDIT.md §3.8.
  const { isItAdmin } = useAuth();
  const canResetJob = isItAdmin;

  const [selectedRowKey, setSelectedRowKey] = useState("");

  const [txtJobNum, setTxtJobNum] = useState("");
  const [txtQuan, setTxtQuan] = useState("");
  const [txtPrintRev, setTxtPrintRev] = useState("");
  const [txtMafiaRev, setTxtMafiaRev] = useState("");
  const [txtSeq, setTxtSeq] = useState("0");
  const [txtPart, setTxtPart] = useState("");

  const [txtNewJobNum, setTxtNewJobNum] = useState("");
  const [txtNewPrintRev, setTxtNewPrintRev] = useState("");
  const [txtNewMAFIARev, setTxtNewMAFIARev] = useState("");
  const [txtNewSeq, setTxtNewSeq] = useState("0");

  useEffect(() => {
    mountedRef.current = true;

    async function initializePage() {
      try {
        const username = await getCurrentUsername();
        if (!mountedRef.current) return;

        setCurrentUsername(username);

        if (searchInputRef.current) {
          searchInputRef.current.focus();
          searchInputRef.current.select();
        }
      } catch (error) {
        console.error("Failed to initialize frmJob:", error);
      }
    }

    initializePage();

    return () => {
      mountedRef.current = false;
    };
  }, []);

  const tableColumns = useMemo(() => {
    if (!rows || rows.length === 0) return [];
    return Object.keys(rows[0]);
  }, [rows]);

  const filteredRows = useMemo(() => {
    const search = searchJobNum.trim().toUpperCase();
    if (!search) return rows;

    return rows.filter((row) =>
      Object.values(row || {}).some((value) =>
        String(value ?? "").toUpperCase().includes(search)
      )
    );
  }, [rows, searchJobNum]);

  const selectedRow = useMemo(() => {
    return rows.find((row) => getRowKey(row) === selectedRowKey) || null;
  }, [rows, selectedRowKey]);

  const clearSelection = useCallback(() => {
    setSelectedRowKey("");
    setTxtJobNum("");
    setTxtQuan("");
    setTxtPrintRev("");
    setTxtMafiaRev("");
    setTxtSeq("0");
    setTxtPart("");
  }, []);

  const refreshJobList = useCallback(
    async (jobnum) => {
      try {
        setLoading(true);
        setRefreshing(true);

        const data = await getJobList({ jobNum: jobnum });
        if (!mountedRef.current) return;

        const safeRows = data || [];
        setRows(safeRows);

        if (selectedRowKey) {
          const stillExists = safeRows.some((row) => getRowKey(row) === selectedRowKey);
          if (!stillExists) {
            clearSelection();
          }
        }
      } catch (error) {
        console.error("Failed to load job list:", error);
        await dialog.error("Failed to load job list.");
      } finally {
        if (mountedRef.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [selectedRowKey, clearSelection, dialog]
  );

  function populateSelectedRow(row) {
    if (!row) return;

    setTxtJobNum(stringOrEmpty(row.jobNum ?? getColumnValue(row, 0)));
    setTxtQuan(stringOrEmpty(row.quan ?? getColumnValue(row, 1)));
    setTxtPrintRev(stringOrEmpty(row.revMaj ?? row.printRev ?? getColumnValue(row, 4)));
    setTxtMafiaRev(stringOrEmpty(row.revMinor ?? row.mafiaRev ?? getColumnValue(row, 5)));

    const seqVal = row.seq ?? getColumnValue(row, 6);
    setTxtSeq(seqVal == null || seqVal === "" ? "0" : String(seqVal).trim());

    const partVal = row.partNumber ?? row.part ?? getColumnValue(row, 7);
    setTxtPart(partVal == null ? "" : String(partVal).trim());
  }

  function selectRow(row) {
    if (!row) return;
    setSelectedRowKey(getRowKey(row));
    populateSelectedRow(row);
  }

  function handleSearchKeyDown(e) {
    if (e.key !== "Enter") return;

    if (filteredRows.length > 0) {
      const firstMatch = filteredRows[0];
      const rowKey = getRowKey(firstMatch);
      setSelectedRowKey(rowKey);
      populateSelectedRow(firstMatch);

      const rowEl = rowRefs.current[rowKey];
      if (rowEl) {
        rowEl.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }

    if (searchInputRef.current) {
      searchInputRef.current.select();
    }
  }

  async function handleUpdateJobHeader() {
    if (
      txtJobNum.trim() !== "" &&
      txtMafiaRev.trim() !== "" &&
      txtPrintRev.trim() !== "" &&
      txtQuan.trim() !== ""
    ) {
      let j = 0;
      let k = 0;

      let seqValue = txtSeq.trim();
      if (seqValue === "") {
        seqValue = "0";
        setTxtSeq("0");
      }

      try {
        j = Number.parseInt(txtPrintRev.trim(), 10);
        k = Number.parseInt(txtMafiaRev.trim(), 10);

        if (Number.isNaN(j) || Number.isNaN(k)) {
          throw new Error("Invalid integer");
        }
      } catch (error) {
        await dialog.warning("Invalid Rev # detected, please try again.");
        return;
      }

      try {
        if (txtPart.trim() !== "") {
          const c = await getMafiaTable({
            partNumber: txtPart.trim(),
            rev: j,
            revMinor: k,
          });

          if (!c || c.length === 0) {
            await dialog.warning(
              "MAFIA table not found for the selected part & rev #'s. Please double check and retry your operation."
            );
            return;
          }
        }

        const success = await updateJobHeader({
          jobNum: txtJobNum.trim(),
          quan: txtQuan.trim(),
          revMaj: txtPrintRev.trim(),
          revMinor: txtMafiaRev.trim(),
          seq: Number(seqValue),
        });

        if (!success) {
          await dialog.error(
            "Error in modifying job header. Please try again or contact technical support on x1776."
          );
        } else {
          await dialog.success("Successfully modified Job Header.");
          await refreshJobList(txtJobNum.trim());
        }
      } catch (error) {
        console.error("Update job header failed:", error);
        await dialog.error(
          "Error in modifying job header. Please try again or contact technical support on x1776."
        );
      }
    }
  }

  async function handleResetJob() {
    if (txtJobNum.trim() === "") {
      await dialog.warning("Please first select a job to delete.");
      return;
    }

    const ans = await dialog.confirm({
      title: "Reset Job",
      message: `Resetting job will erase all recorded pieces, comments, and any associated entries with the job #${txtJobNum.trim()}. Are you sure you wish to continue?`,
      confirmText: "Continue",
      cancelText: "Cancel",
      type: "warning",
    });
    if (!ans) return;

    const ans2 = await dialog.confirm({
      title: "Confirm Operator Exit",
      message:
        "Please ensure all operators / inspectors are currently out of this job before continuing. Are you ready to continue?",
      confirmText: "Yes, Continue",
      cancelText: "Cancel",
      type: "warning",
    });
    if (!ans2) return;

    try {
      const seq = Number.parseInt(txtSeq || "0", 10);

      await deleteJobHeader({ jobNum: txtJobNum.trim(), seq });
      await deleteMafiaEntries({ jobNum: txtJobNum.trim(), seq });
      await deleteMafiaEntriesComments({ jobNum: txtJobNum.trim(), seq });
      const opDeleted = await deleteOpHeader({ jobNum: txtJobNum.trim(), seq });

      if (opDeleted) {
        await dialog.success(
          "Job has been reset. All operator / q-final entries have been removed."
        );
        await refreshJobList(txtJobNum.trim());
      }
    } catch (error) {
      console.error("Reset job failed:", error);
      await dialog.error("Failed to reset job.");
    }
  }

  async function handleNewJobOverride() {
    let revMaj = 0;
    let revMin = 0;

    try {
      revMaj = Number.parseInt(txtNewPrintRev, 10);
      revMin = Number.parseInt(txtNewMAFIARev, 10);

      if (Number.isNaN(revMaj) || Number.isNaN(revMin)) {
        throw new Error("Invalid rev");
      }
    } catch (error) {
      await dialog.warning(
        "Invalid Rev # detected. Please double check your entry and try again."
      );
      return;
    }

    try {
      const originalQuantity = await getOriginalQuantity(
        txtNewJobNum.trim(),
        1,
        Number.parseInt(txtNewSeq || "0", 10)
      );

      if (Number(originalQuantity) !== 0) {
        await dialog.warning(
          "It seems like this isn't a new job -- entries have already started with this job number. Please use the 'Inspection Already Started' section to edit Rev #"
        );
        return;
      }

      if (
        Number.parseInt(txtNewPrintRev, 10) === 0 ||
        Number.parseInt(txtNewMAFIARev, 10) === 0 ||
        txtNewJobNum.trim() === ""
      ) {
        await dialog.warning("Invalid Rev # or Job # detected. Operation Aborted");
        return;
      }

      const seq = Number.parseInt(txtNewSeq || "0", 10);
      const quan = await getQuantity({
        jobNum: txtNewJobNum.trim(),
        seq,
      });

      const success = await insertHeader({
        jobNum: txtNewJobNum.trim(),
        waveNumber: 1,
        origQuan: Number(quan),
        check100: false,
        revMin: Number.parseInt(txtNewMAFIARev.trim(), 10),
        revMaj: Number.parseInt(txtNewPrintRev.trim(), 10),
        seq,
      });

      if (!success) {
        await dialog.error(
          "Error in adding job header, please contact technical support, x1776."
        );
      } else {
        await dialog.success("Override successful!");
      }
    } catch (error) {
      console.error("Insert header failed:", error);
      await dialog.error(
        "Error in adding job header, please contact technical support, x1776."
      );
    }
  }

  return (
    <div className="frm-job-page mafia-screen">
      <MafiaTheme />
      <MafiaPageHeader
        icon="ri-briefcase-4-line"
        eyebrow="MAFIA Aux"
        title="Job Lookup"
        subtitle="Find part records by job number and operation."
        tone="blue"
        actions={
          <Button
            color="light"
            onClick={onBack}
            style={{
              background: "rgba(255,255,255,0.92)",
              border: "none",
              fontWeight: 500,
            }}
          >
            <i className="ri-arrow-left-line align-bottom me-1" />
            Back to Menu
          </Button>
        }
        breadcrumb={[
          { label: "Applications", to: "/" },
          { label: "MAFIA Aux", onClick: onBack },
          { label: "Job" },
        ]}
      />

      <Row className="g-4">
        <Col xl={12}>
          <Card className="border-0 shadow-sm">
            <CardHeader className="bg-white border-0 pt-4 px-4 pb-3">
              <div className="frm-job-toolbar-main">
                <div className="frm-job-toolbar-main-left">
                  {/* Back moved to page header */}
                </div>

                <div className="frm-job-toolbar-main-right">
                  <InputGroup className="frm-job-search-group">
                    <InputGroupText className="bg-light border-end-0">
                      <i className="ri-search-line" />
                    </InputGroupText>
                    <Input
                      innerRef={searchInputRef}
                      type="text"
                      value={searchJobNum}
                      onChange={(e) => setSearchJobNum(e.target.value)}
                      onKeyDown={handleSearchKeyDown}
                      className="border-start-0"
                      placeholder="Search job number"
                    />
                  </InputGroup>
                </div>
              </div>

              <div className="frm-job-toolbar-utility">
                <Button
                  color="primary"
                  className="frm-job-action-btn frm-job-utility-btn"
                  onClick={() => refreshJobList(searchJobNum.trim())}
                  disabled={refreshing}
                >
                  <i className="ri-search-line frm-job-btn-icon me-1" />
                  {refreshing ? "Searching..." : "Search Jobs"}
                </Button>

                <Button
                  color="success"
                  className="frm-job-action-btn frm-job-utility-btn"
                  onClick={handleUpdateJobHeader}
                >
                  <i className="ri-save-line frm-job-btn-icon me-1" />
                  Update Job Header
                </Button>

                <Button
                  color="danger"
                  outline
                  className="frm-job-action-btn frm-job-utility-btn"
                  onClick={handleResetJob}
                  disabled={!canResetJob}
                >
                  <i className="ri-refresh-line frm-job-btn-icon me-1" />
                  Reset Job
                </Button>

                <Button
                  color="dark"
                  className="frm-job-action-btn frm-job-utility-btn"
                  onClick={handleNewJobOverride}
                >
                  <i className="ri-add-circle-line frm-job-btn-icon me-1" />
                  New Job Override
                </Button>

                <Badge color="light" className="frm-job-queue-badge">
                  Queue Size: {filteredRows.length}
                </Badge>

                <div className="frm-job-info-pill">
                  <span className="frm-job-info-label">Selected Job</span>
                  <span className="frm-job-info-value" title={txtJobNum || "No selection"}>
                    {txtJobNum || "No selection"}
                  </span>
                </div>

                <div className="frm-job-info-pill">
                  <span className="frm-job-info-label">Revision</span>
                  <span className="frm-job-info-value">
                    {txtPrintRev || "—"}.{txtMafiaRev || "—"}
                  </span>
                </div>

                <div className="frm-job-info-pill">
                  <span className="frm-job-info-label">Reset Access</span>
                  <span className="frm-job-info-value">
                    {canResetJob ? "Enabled" : "Restricted"}
                  </span>
                </div>

                <div className="frm-job-refresh-note">
                  User: {currentUsername || "Unknown"}
                </div>
              </div>
            </CardHeader>

            <CardBody className="p-4">
              <div className="frm-job-table-shell mb-4">
                <div className="frm-job-table-scroll">
                  <Table responsive={false} className="align-middle mb-0 frm-job-table">
                    <thead className="table-light">
                      <tr>
                        <th className="frm-job-col-select">Select</th>
                        {tableColumns.length > 0 ? (
                          tableColumns.map((col) => <th key={col}>{col}</th>)
                        ) : (
                          <th>Results</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={(tableColumns.length || 1) + 1} className="text-center py-5 text-muted">
                            Loading jobs...
                          </td>
                        </tr>
                      ) : filteredRows.length === 0 ? (
                        <tr>
                          <td colSpan={(tableColumns.length || 1) + 1} className="text-center py-5 text-muted">
                            No jobs found.
                          </td>
                        </tr>
                      ) : (
                        filteredRows.map((row, rowIndex) => {
                          const rowKey = getRowKey(row);
                          const isSelected = rowKey === selectedRowKey;

                          return (
                            <tr
                              key={`${rowKey}#${rowIndex}`}
                              ref={(el) => {
                                if (el) {
                                  rowRefs.current[rowKey] = el;
                                }
                              }}
                              className={`frm-job-data-row ${isSelected ? "selected" : ""}`}
                              onClick={() => selectRow(row)}
                              onDoubleClick={() => selectRow(row)}
                            >
                              <td className="frm-job-col-select">
                                <Input
                                  type="radio"
                                  checked={isSelected}
                                  onChange={() => selectRow(row)}
                                  name="selected-job-row"
                                />
                              </td>
                              {tableColumns.map((col) => (
                                <td key={col}>{formatAny(row[col])}</td>
                              ))}
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>

              <Row className="g-4">
                <Col xl={6}>
                  <div className="frm-job-panel-card h-100">
                    <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
                      <div>
                        <h5 className="mb-1">Inspection Already Started</h5>
                        <p className="text-muted mb-0">
                          Update the selected job header details for an existing inspection.
                        </p>
                      </div>
                      <Badge color="light" className="frm-job-soft-badge text-uppercase">Update</Badge>
                    </div>

                    <Row className="g-3">
                      <Col md={6}><Field label="Job Num" value={txtJobNum} onChange={setTxtJobNum} /></Col>
                      <Col md={6}><Field label="Quan" value={txtQuan} onChange={setTxtQuan} /></Col>
                      <Col md={6}><Field label="Drawing Revision" value={txtPrintRev} onChange={setTxtPrintRev} /></Col>
                      <Col md={6}><Field label="MAFIA Revision" value={txtMafiaRev} onChange={setTxtMafiaRev} /></Col>
                      <Col md={6}><Field label="Seq" value={txtSeq} onChange={setTxtSeq} /></Col>
                      <Col md={6}><Field label="Part" value={txtPart} onChange={setTxtPart} /></Col>
                    </Row>

                    <div className="d-flex flex-wrap gap-2 mt-4">
                      <Button color="success" className="frm-job-action-btn" onClick={handleUpdateJobHeader}>
                        <i className="ri-save-line frm-job-btn-icon me-1" />
                        Update Job Header
                      </Button>
                      <Button
                        color="danger"
                        outline
                        className="frm-job-action-btn"
                        onClick={handleResetJob}
                        disabled={!canResetJob}
                      >
                        <i className="ri-refresh-line frm-job-btn-icon me-1" />
                        Reset Job
                      </Button>
                    </div>
                  </div>
                </Col>

                <Col xl={6}>
                  <div className="frm-job-panel-card h-100">
                    <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
                      <div>
                        <h5 className="mb-1">New Job Override</h5>
                        <p className="text-muted mb-0">
                          Insert a fresh job header when inspection has not started yet.
                        </p>
                      </div>
                      <Badge color="light" className="frm-job-soft-badge text-uppercase">Override</Badge>
                    </div>

                    <Row className="g-3">
                      <Col md={6}><Field label="New Job Num" value={txtNewJobNum} onChange={setTxtNewJobNum} /></Col>
                      <Col md={6}><Field label="New Drawing Revision" value={txtNewPrintRev} onChange={setTxtNewPrintRev} /></Col>
                      <Col md={6}><Field label="New MAFIA Revision" value={txtNewMAFIARev} onChange={setTxtNewMAFIARev} /></Col>
                      <Col md={6}><Field label="New Seq" value={txtNewSeq} onChange={setTxtNewSeq} /></Col>
                    </Row>

                    <div className="d-flex flex-wrap gap-2 mt-4">
                      <Button color="dark" className="frm-job-action-btn" onClick={handleNewJobOverride}>
                        <i className="ri-add-circle-line frm-job-btn-icon me-1" />
                        Override
                      </Button>
                    </div>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <style>{`
        .frm-job-toolbar-main {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          flex-wrap: wrap;
          margin-bottom: 0.9rem;
        }

        .frm-job-toolbar-main-left {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .frm-job-toolbar-main-right {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          flex: 0 0 360px;
          min-width: 280px;
        }

        .frm-job-toolbar-utility {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
          padding-top: 0.25rem;
        }

        .frm-job-search-group {
          width: 100%;
        }

        .frm-job-soft-badge {
          border: 1px solid #e9ebec;
          padding: 0.55rem 0.85rem;
          font-size: 0.72rem;
          letter-spacing: 0.06em;
          border-radius: 999px;
        }

        .frm-job-panel-card {
          background: rgba(255,255,255,0.92);
          border: 1px solid #e9ebec;
          border-radius: 1rem;
          padding: 1rem 1.1rem;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04);
          height: 100%;
        }

        .frm-job-action-btn {
            white-space: nowrap;
          }
          /* Make disabled primary buttons obviously disabled (not faded primary) */
          .frm-job-action-btn:disabled,
          .frm-job-action-btn.disabled {
            background-color: #e9ebec !important;
            border-color: #e9ebec !important;
            color: #878a99 !important;
            opacity: 1 !important;
          }


        .frm-job-utility-btn {
            min-width: 110px;
          }

        .frm-job-btn-icon {
          font-size: 0.95rem;
        }

        .btn-soft-dark {
          background: #eff2f7;
          color: #212529;
          border: 1px solid #e9ebec;
        }

        .btn-soft-dark:hover,
        .btn-soft-dark:focus {
          background: #e9ebec;
          color: #212529;
          border-color: #dee2e6;
        }

        .frm-job-queue-badge {
            display: inline-flex;
            align-items: center;
            min-height: 32px;
            border: 1px solid #e9ebec;
            padding: 0.3rem 0.65rem;
            border-radius: 0.375rem;
            font-size: 0.8125rem;
            font-weight: 600;
            color: #495057;
            background: #f3f6f9;
          }

        .frm-job-info-pill {
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            min-height: 34px;
            padding: 0.3rem 0.75rem;
            border: 1px solid #e9ebec;
            border-radius: 0.5rem;
            background: #f3f6f9;
            font-family: inherit;
            max-width: 300px;
          }

        .frm-job-info-label {
            font-family: inherit;
            font-size: 0.6875rem;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            color: #878a99;
            white-space: nowrap;
            line-height: 1.1;
          }

        .frm-job-info-value {
            font-family: inherit;
            font-size: 0.8125rem;
            font-weight: 600;
            color: #495057;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

        .frm-job-refresh-note {
          min-height: 38px;
          display: inline-flex;
          align-items: center;
          padding: 0.45rem 0.2rem;
          font-size: 0.84rem;
          color: #878a99;
          font-weight: 500;
        }

        .frm-job-table-shell {
          border: 1px solid #e9ebec;
          border-radius: 1rem;
          background: #fff;
        }

        .frm-job-table-scroll {
          max-height: calc(100vh - 260px);
          min-height: 320px;
          overflow-x: auto;
          overflow-y: auto;
        }

        .frm-job-table {
          min-width: 1100px;
        }

        .frm-job-table thead th {
          position: sticky;
          top: 0;
          z-index: 2;
          background: #f3f6f9;
          white-space: nowrap;
        }

        .frm-job-table tbody td {
          padding: 0.9rem 0.9rem;
          border-top: 1px solid #eef2f7;
          color: #495057;
          vertical-align: middle;
          white-space: nowrap;
        }

        .frm-job-col-select {
          width: 72px;
          text-align: center;
        }

        .frm-job-data-row {
          transition: background-color 0.18s ease, box-shadow 0.18s ease;
          cursor: pointer;
        }

        .frm-job-data-row:nth-of-type(even) {
          background: #fbfcfe;
        }

        .frm-job-data-row:hover {
          background: #f5f9ff;
        }

        .frm-job-data-row.selected {
          background: rgba(13, 110, 253, 0.08);
          box-shadow: inset 4px 0 0 #0d6efd;
        }

        @media (max-width: 1199.98px) {
          .frm-job-toolbar-main {
            flex-direction: column;
            align-items: stretch;
          }

          .frm-job-toolbar-main-right {
            flex: 1 1 auto;
            min-width: 100%;
          }
        }

        @media (max-width: 991.98px) {
          .frm-job-info-pill {
            display: inline-flex;
            align-items: center;
            gap: 0.4rem;
            min-height: 32px;
            padding: 0.3rem 0.65rem;
            border: 1px solid #e9ebec;
            border-radius: 0.375rem;
            background: #f3f6f9;
            max-width: 300px;
          }

          .frm-job-toolbar-utility {
            align-items: stretch;
          }
        }
      `}</style>
    </div>
  );
}

function Field({ label, value, onChange }) {
  return (
    <div>
      <Label className="form-label">{label}</Label>
      <Input type="text" value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function stringOrEmpty(value) {
  if (value == null) return "";
  return String(value).trim();
}

function formatAny(value) {
  if (value == null) return "";
  return String(value);
}

function getColumnValue(row, index) {
  if (!row) return null;
  const values = Object.values(row);
  if (index < 0 || index >= values.length) return null;
  return values[index];
}

function getRowKey(row) {
  // Round 13: the previous key was Job-AssmSeq-Part. job-list can return
  // several header rows that share the same Job, Assm Seq (0) and an empty
  // Part but differ by Print Rev / MAFIA Rev — these collided, producing the
  // React "two children with the same key" warning and risking dropped or
  // duplicated rows. Include Print Rev (col 4) and MAFIA Rev (col 5) so the
  // key is unique per logical row while staying stable across re-renders
  // (selection relies on this stability).
  const job = stringOrEmpty(row?.jobNum ?? getColumnValue(row, 0));
  const seq = stringOrEmpty(row?.seq ?? getColumnValue(row, 6));
  const part = stringOrEmpty(row?.partNumber ?? getColumnValue(row, 7));
  const printRev = stringOrEmpty(row?.printRev ?? getColumnValue(row, 4));
  const mafiaRev = stringOrEmpty(row?.mafiaRev ?? getColumnValue(row, 5));
  return `${job}-${seq}-${part}-${printRev}-${mafiaRev}`;
}

async function getCurrentUsername() {
  return getMafiaEmpId();
}

async function getJobList(req) {
  const response = await api.post("mafia/job-list", {
    jobNum: req.jobNum,
  });
  return response.data;
}

async function getMafiaTable(req) {
  const response = await api.post("mafia/mafiatable", {
    partNumber: req.partNumber,
    rev: req.rev,
    revMinor: req.revMinor,
  });
  return response.data;
}

async function updateJobHeader(req) {
  const response = await api.post("mafia/update-jobheader", {
    jobNum: req.jobNum,
    quan: req.quan,
    revMaj: req.revMaj,
    revMinor: req.revMinor,
    seq: req.seq,
  });
  return response.data;
}

async function deleteJobHeader(req) {
  const response = await api.post("mafia/delete-jobheader", {
    jobNum: req.jobNum,
    seq: req.seq,
  });
  return response.data;
}

async function deleteMafiaEntries(req) {
  const response = await api.post("mafia/delete-mafiaentries", {
    jobNum: req.jobNum,
    seq: req.seq,
  });
  return response.data;
}

async function deleteMafiaEntriesComments(req) {
  const response = await api.post("mafia/delete-mafiaentries-comments", {
    jobNum: req.jobNum,
    seq: req.seq,
  });
  return response.data;
}

async function deleteOpHeader(req) {
  const response = await api.post("mafia/delete-opheader", {
    jobNum: req.jobNum,
    seq: req.seq,
  });
  return response.data;
}

async function getOriginalQuantity(jobnum, wavenumber, seq) {
  const response = await api.get("mafia/original-quantity", {
    params: {
      jobnum,
      wavenumber,
      seq,
    },
  });
  return response.data;
}

async function insertHeader(req) {
  const response = await api.post("mafia/insert-header", {
    jobNum: req.jobNum,
    waveNumber: req.waveNumber,
    origQuan: req.origQuan,
    check100: req.check100,
    revMin: req.revMin,
    revMaj: req.revMaj,
    seq: req.seq,
  });
  return response.data;
}

async function getQuantity(req) {
  const response = await api.post("vantage/quantity", {
    jobNum: req.jobNum,
    seq: req.seq,
  });
  return response.data;
}